import React, { useEffect, useState } from "react";
import CircularProgress from "@material-ui/core/CircularProgress";

import { api } from "./api";
import { Bar, Btn, Delta, Kpi, Kpis, Panel, Strip, money, num, pct, precio } from "./ui";

const COLOR_HALLAZGO = {
  pierde: "#D92D20",
  sin_compras: "#D92D20",
  pais_que_quema: "#D92D20",
  cola_sin_compras: "#CA8A04",
  creativo_que_quema: "#D92D20",
  peor_que_el_resto: "#D92D20",
  empeorando: "#D92D20",
  fatiga: "#CA8A04",
  sin_aprendizaje: "#CA8A04",
  concentracion: "#0369A1",
  rinde: "#16A34A",
  mejor_que_el_resto: "#16A34A",
  mejorando: "#16A34A",
};

const ETIQUETA = {
  pierde: "PIERDE",
  sin_compras: "SIN COMPRAS",
  pais_que_quema: "PAÍS QUE QUEMA",
  cola_sin_compras: "COLA SIN COMPRAS",
  creativo_que_quema: "CREATIVO QUE QUEMA",
  peor_que_el_resto: "PEOR QUE EL RESTO",
  mejor_que_el_resto: "MEJOR QUE EL RESTO",
  empeorando: "EMPEORANDO",
  mejorando: "MEJORANDO",
  fatiga: "FATIGA",
  sin_aprendizaje: "SIN APRENDIZAJE",
  concentracion: "CONCENTRACIÓN",
  rinde: "RINDE",
};

/** Celda numérica: todas las columnas de números se alinean a la derecha. */
const N = ({ children, style }) => <td style={{ textAlign: "right", ...style }}>{children}</td>;

const roasColor = (r) => (r >= 1.5 ? "#16A34A" : r < 0.9 ? "#D92D20" : "#24303F");

/**
 * Una campaña en detalle, dentro de la ventana de fechas de la pantalla.
 *
 * La tabla general contesta "cuál anda mal"; esto contesta "por qué", y por eso desarma la
 * campaña por los cuatro ejes sobre los que se puede hacer algo —día, conjunto, creativo y
 * país— en vez de mostrar un promedio más grande. Un CPA malo a nivel campaña casi siempre es
 * un país que quema o un creativo que se llevó el presupuesto, y promediados no se ven.
 *
 * Los días vienen de arriba y no se pueden cambiar acá a propósito: la comparación contra el
 * resto de la cuenta sólo significa algo si las dos mitades miran la misma ventana.
 */
export default function Campana({ id, dias, onVolver }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let vivo = true;
    setD(null);
    setError(null);
    api.campana(id, dias).then(
      (r) => vivo && setD(r),
      (e) => vivo && setError(e.message)
    );
    return () => { vivo = false; };
  }, [id, dias]);

  const volver = <Btn onClick={onVolver}>← volver</Btn>;

  if (error) return <div><Bar>{volver}</Bar><Strip tone="error">{error}</Strip></div>;
  if (!d) return <div><Bar>{volver}</Bar><CircularProgress size={22} /></div>;

  const cab = (
    <Bar style={{ borderBottom: "1px solid #E3E8F0", paddingBottom: 8 }}>
      {volver}
      <div style={{ fontSize: 14, fontWeight: 700, color: "#24303F" }}>{d.campana.nombre}</div>
      <span
        style={{
          fontSize: 10, fontWeight: 700, letterSpacing: ".04em", padding: "2px 6px", borderRadius: 4,
          background: d.campana.corriendo ? "#F0FDF6" : "#F2F5FA",
          color: d.campana.corriendo ? "#10643A" : "#7A8699",
        }}
      >
        {d.campana.corriendo ? "CORRIENDO" : "APAGADA"}
      </span>
      <div style={{ flex: 1 }} />
      <span style={{ fontSize: 11.5, color: "#7A8699" }}>
        {d.ventana.desde} → {d.ventana.hasta} ({d.ventana.dias}d) · id {d.campana.id}
      </span>
    </Bar>
  );

  /*
   * Campaña que existe pero no entregó nada en esta ventana. No es un error: es el caso
   * normal de una campaña vieja, y decir CUÁNDO corrió por última vez deja obvio el paso
   * siguiente. Un "no hay datos" pelado no.
   */
  if (d.sin_datos) {
    return (
      <div>
        {cab}
        <Strip tone="warn">
          Esta campaña no tuvo entrega entre <b>{d.ventana.desde}</b> y <b>{d.ventana.hasta}</b>.
          Su último día con datos es <b>{d.ultimo_dia}</b>: ampliá el rango de arriba para verla.
        </Strip>
      </div>
    );
  }

  const t = d.totales;
  const v = d.ventana;
  const ctx = d.contexto;
  const parcial = v.dias_con_datos < v.dias;
  const gastoMaxDia = Math.max(...d.serie.map((s) => s.gasto), 0);

  return (
    <div>
      {cab}

      {/*
        Días CON entrega, no días de la ventana. Sin esto, "$391 en 30 días" se lee como un
        ritmo de $13/día cuando en realidad fueron 8 días a $49, que es otra campaña.
      */}
      <Strip tone={parcial ? "warn" : "info"}>
        Entregó <b>{v.dias_con_datos}</b> de los {v.dias} días de la ventana
        {v.primer_dia && <> (<b>{v.primer_dia}</b> → <b>{v.ultimo_dia}</b>)</>}, a{" "}
        <b>{money(v.gasto_por_dia_activo)}</b> por día activo.
        {parcial && " El promedio sobre la ventana entera diría bastante menos."}
      </Strip>

      <Panel title="La campaña en la ventana" pad={0}>
        <Kpis>
          <Kpi label="gasto" value={money(t.gasto)} sub={`${num(t.impresiones)} impr.`} />
          <Kpi label="ingreso" value={money(t.valor)} sub={`ticket ${precio(t.ticket)}`} />
          <Kpi label="roas" value={Number(t.roas).toFixed(2)} tone={t.roas >= 1.5 ? "ok" : t.roas < 0.9 ? "bad" : undefined} sub={`vs resto ${ctx.vs.roas == null ? "—" : (ctx.vs.roas > 0 ? "+" : "") + ctx.vs.roas.toFixed(0) + "%"}`} />
          <Kpi label="compras" value={num(t.compras)} sub={`CPA ${precio(t.cpa)}`} />
          <Kpi label="ctr" value={pct(t.ctr)} sub={`${num(t.clicks)} clicks`} />
          <Kpi label="cpc" value={precio(t.cpc)} sub={`CPM ${precio(t.cpm)}`} />
          <Kpi label="enganche" value={pct(t.enganche, 1)} sub="thruplays / impr." />
          <Kpi label="frecuencia" value={Number(t.frecuencia_max).toFixed(2)} tone={t.frecuencia_max >= 2.5 ? "warn" : undefined} sub="máx. diaria" />
          <Kpi label="del gasto" value={pct(ctx.share_gasto, 0)} sub="de la cuenta" />
        </Kpis>
      </Panel>

      {d.hallazgos.length > 0 && (
        <Panel title={`Hallazgos de esta campaña (${d.hallazgos.length})`} pad={0}>
          {d.hallazgos.map((h, i) => (
            <div
              key={i}
              style={{
                display: "flex", gap: 8, alignItems: "baseline", padding: "5px 10px",
                borderBottom: i < d.hallazgos.length - 1 ? "1px solid #F2F5FA" : "none",
                fontSize: 12.5,
              }}
            >
              <span style={{ color: COLOR_HALLAZGO[h.tipo] || "#334155", fontWeight: 800, fontSize: 10.5, minWidth: 140 }}>
                {ETIQUETA[h.tipo] || h.tipo}
              </span>
              <span style={{ flex: 1 }}>
                {h.detalle}
                <span style={{ color: "#A0AABA", marginLeft: 6 }}>({h.umbral})</span>
              </span>
              <span style={{ fontWeight: 700, color: "#46536B", whiteSpace: "nowrap" }}>
                {h.impacto ? money(h.impacto) : ""}
              </span>
            </div>
          ))}
        </Panel>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 10 }}>
        {/*
          El embudo con la caída de cada tramo: es lo que separa "no llega gente" de "llega y
          no compra", que se arreglan en lugares distintos (creativo vs. página de pago).
        */}
        <Panel title="Embudo" pad={0} style={{ marginBottom: 0 }}>
          {!d.embudo.intermedios_disponibles && (
            <Strip tone="warn" style={{ margin: 10, marginBottom: 0 }}>
              Los pasos intermedios están en cero porque todavía no se importaron: esas columnas
              se agregaron el 06/08/2026 y sólo se llenan en las corridas nuevas de{" "}
              <code>meta:import-ads</code>. No significa que nadie haya llegado a la página.
            </Strip>
          )}
          <div style={{ overflowX: "auto" }}>
          <table>
            <thead>
              <tr>
                <th style={{ textAlign: "left" }}>paso</th>
                <th style={{ textAlign: "right" }}>cantidad</th>
                <th style={{ textAlign: "right" }}>del anterior</th>
                <th style={{ textAlign: "right" }}>costo c/u</th>
              </tr>
            </thead>
            <tbody>
              {d.embudo.pasos.map((p) => {
                const vacio = p.valor === 0 && !d.embudo.intermedios_disponibles
                  && ["vistas_landing", "carritos", "pagos"].includes(p.clave);
                return (
                  <tr key={p.clave} style={vacio ? { opacity: 0.4 } : undefined}>
                    <td>{p.etiqueta}</td>
                    <N>{vacio ? "s/d" : num(p.valor)}</N>
                    <N style={{ color: "#7A8699" }}>{vacio || p.de_anterior == null ? "—" : pct(p.de_anterior)}</N>
                    <N>{vacio ? "—" : precio(p.costo)}</N>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </Panel>

        {/*
          Contra el resto de la cuenta, no contra el total: si esta campaña se lleva la mayor
          parte del gasto, compararla contra el total sería compararla contra sí misma.
        */}
        <Panel title="Contra el resto de la cuenta (misma ventana)" pad={0} style={{ marginBottom: 0 }}>
          <div style={{ overflowX: "auto" }}>
          <table>
            <thead>
              <tr>
                <th style={{ textAlign: "left" }}>métrica</th>
                <th style={{ textAlign: "right" }}>esta campaña</th>
                <th style={{ textAlign: "right" }}>el resto</th>
                <th style={{ textAlign: "right" }}>dif.</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>ROAS</td>
                <N style={{ fontWeight: 700, color: roasColor(t.roas) }}>{Number(t.roas).toFixed(2)}</N>
                <N>{Number(ctx.resto.roas).toFixed(2)}</N>
                <N><Delta valor={ctx.vs.roas} /></N>
              </tr>
              <tr>
                {/* En CPA y CPM, más barato es mejor: el signo bueno es el negativo. */}
                <td>CPA</td>
                <N style={{ fontWeight: 700 }}>{precio(t.cpa)}</N>
                <N>{precio(ctx.resto.cpa)}</N>
                <N><Delta valor={ctx.vs.cpa} mejorSiSube={false} /></N>
              </tr>
              <tr>
                <td>CTR</td>
                <N style={{ fontWeight: 700 }}>{pct(t.ctr)}</N>
                <N>{pct(ctx.resto.ctr)}</N>
                <N><Delta valor={ctx.vs.ctr} /></N>
              </tr>
              <tr>
                <td>CPM</td>
                <N style={{ fontWeight: 700 }}>{precio(t.cpm)}</N>
                <N>{precio(ctx.resto.cpm)}</N>
                <N><Delta valor={ctx.vs.cpm} mejorSiSube={false} /></N>
              </tr>
              <tr>
                <td>gasto</td>
                <N style={{ fontWeight: 700 }}>{money(t.gasto)}</N>
                <N>{money(ctx.resto.gasto)}</N>
                <N style={{ color: "#7A8699" }}>{pct(ctx.share_gasto, 0)} del total</N>
              </tr>
            </tbody>
          </table>
          </div>
        </Panel>
      </div>

      {/*
        Primera mitad contra segunda: es lo único que distingue "rinde mal" de "rendía bien y
        se está apagando", que piden decisiones opuestas. Se parte por días CON entrega, no
        por fecha, para que una campaña que corrió una semana de un mes no compare contra vacío.
      */}
      {d.tendencia && (
        <Panel title="Cómo viene: primera mitad contra segunda" pad={0} style={{ marginTop: 10 }}>
          <div style={{ overflowX: "auto" }}>
          <table>
            <thead>
              <tr>
                <th style={{ textAlign: "left" }}>tramo</th>
                <th style={{ textAlign: "right" }}>días</th>
                <th style={{ textAlign: "right" }}>gasto</th>
                <th style={{ textAlign: "right" }}>compras</th>
                <th style={{ textAlign: "right" }}>ROAS</th>
                <th style={{ textAlign: "right" }}>CPA</th>
                <th style={{ textAlign: "right" }}>CTR</th>
                <th style={{ textAlign: "right" }}>CPM</th>
              </tr>
            </thead>
            <tbody>
              {["primera", "segunda"].map((m) => (
                <tr key={m}>
                  <td>{d.tendencia[m].desde} → {d.tendencia[m].hasta}</td>
                  <N>{d.tendencia[m].dias}</N>
                  <N>{money(d.tendencia[m].gasto)}</N>
                  <N>{d.tendencia[m].compras}</N>
                  <N style={{ fontWeight: 700, color: roasColor(d.tendencia[m].roas) }}>{Number(d.tendencia[m].roas).toFixed(2)}</N>
                  <N>{precio(d.tendencia[m].cpa)}</N>
                  <N>{pct(d.tendencia[m].ctr)}</N>
                  <N>{precio(d.tendencia[m].cpm)}</N>
                </tr>
              ))}
              <tr style={{ background: "#F7F9FC" }}>
                <td style={{ fontWeight: 700 }}>cambio</td>
                <N />
                <N><Delta valor={d.tendencia.delta.gasto} /></N>
                <N />
                <N><Delta valor={d.tendencia.delta.roas} /></N>
                <N><Delta valor={d.tendencia.delta.cpa} mejorSiSube={false} /></N>
                <N><Delta valor={d.tendencia.delta.ctr} /></N>
                <N><Delta valor={d.tendencia.delta.cpm} mejorSiSube={false} /></N>
              </tr>
            </tbody>
          </table>
          </div>
        </Panel>
      )}

      <Panel
        title="Día por día"
        right={<span style={{ fontSize: 11, color: "#7A8699", textTransform: "none", letterSpacing: 0, fontWeight: 400 }}>{v.dias_con_datos} día(s) con entrega</span>}
        pad={0}
      >
        <div style={{ maxHeight: 320, overflow: "auto" }}>
          <table>
            <thead>
              <tr>
                <th style={{ textAlign: "left" }}>día</th>
                <th style={{ textAlign: "left", width: "26%" }}>gasto</th>
                <th style={{ textAlign: "right" }}>impr.</th>
                <th style={{ textAlign: "right" }}>clicks</th>
                <th style={{ textAlign: "right" }}>CTR</th>
                <th style={{ textAlign: "right" }}>compras</th>
                <th style={{ textAlign: "right" }}>ingreso</th>
                <th style={{ textAlign: "right" }}>ROAS</th>
                <th style={{ textAlign: "right" }}>CPA</th>
                <th style={{ textAlign: "right" }}>freq</th>
              </tr>
            </thead>
            <tbody>
              {d.serie.map((s) => (
                <tr key={s.fecha} title={`${s.fecha}: ${money(s.gasto)} · ${s.compras} compra(s) · ROAS ${Number(s.roas).toFixed(2)}`}>
                  <td style={{ whiteSpace: "nowrap" }}>{s.fecha}</td>
                  {/*
                    La barra es para comparar de un vistazo; el número al lado es para leer.
                    Una sola serie, así que un solo tono: acá el color no codifica nada.
                  */}
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div className="ma-bar-bg" style={{ flex: 1 }}>
                        <div className="ma-bar" style={{ width: gastoMaxDia > 0 ? `${(s.gasto / gastoMaxDia) * 100}%` : "1px" }} />
                      </div>
                      <span style={{ minWidth: 46, textAlign: "right" }}>{money(s.gasto)}</span>
                    </div>
                  </td>
                  <N>{num(s.impresiones)}</N>
                  <N>{num(s.clicks)}</N>
                  <N>{pct(s.ctr)}</N>
                  <N style={{ fontWeight: s.compras > 0 ? 700 : 400, color: s.compras > 0 ? "#24303F" : "#A0AABA" }}>{s.compras}</N>
                  <N>{s.valor > 0 ? money(s.valor) : "—"}</N>
                  <N style={{ fontWeight: 700, color: roasColor(s.roas) }}>{Number(s.roas).toFixed(2)}</N>
                  <N>{precio(s.cpa)}</N>
                  <N>{Number(s.frecuencia_max).toFixed(2)}</N>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      {/*
        Conjuntos. Acá vive el presupuesto y el aprendizaje: es el nivel donde el problema
        puede ser estructural (demasiados conjuntos para el volumen) y ningún creativo lo salva.
      */}
      <Panel title={`Conjuntos (${d.adsets.length})`} pad={0}>
        <div style={{ overflowX: "auto" }}>
        <table>
          <thead>
            <tr>
              <th style={{ textAlign: "left" }}>conjunto</th>
              <th style={{ textAlign: "right" }}>% gasto</th>
              <th style={{ textAlign: "right" }}>gasto</th>
              <th style={{ textAlign: "right" }}>días</th>
              <th style={{ textAlign: "right" }}>anuncios</th>
              <th style={{ textAlign: "right" }}>compras</th>
              <th style={{ textAlign: "right" }}>ROAS</th>
              <th style={{ textAlign: "right" }}>CPA</th>
              <th style={{ textAlign: "right" }}>CTR</th>
              <th style={{ textAlign: "right" }}>freq</th>
              <th style={{ textAlign: "left" }}>aprendizaje (7d)</th>
            </tr>
          </thead>
          <tbody>
            {d.adsets.map((a) => (
              <tr key={a.clave}>
                <td style={{ maxWidth: 260 }}>
                  <div style={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={a.nombre}>
                    {a.nombre}
                    {!a.corriendo && <span style={{ fontSize: 10, color: "#A0AABA", fontWeight: 400 }}> · apagado</span>}
                  </div>
                </td>
                <N style={{ color: "#7A8699" }}>{pct(a.share, 0)}</N>
                <N>{money(a.gasto)}</N>
                <N>{a.dias}</N>
                <N>{a.copias}</N>
                <N>{a.compras}</N>
                <N style={{ fontWeight: 700, color: roasColor(a.roas) }}>{Number(a.roas).toFixed(2)}</N>
                <N>{precio(a.cpa)}</N>
                <N>{pct(a.ctr)}</N>
                <N>{Number(a.frecuencia_reciente || a.frecuencia_max).toFixed(2)}</N>
                <td style={{ color: a.aprendizaje.llega ? "#10643A" : "#8A6D1F", whiteSpace: "nowrap" }}>
                  {a.aprendizaje.compras_7d} / {a.aprendizaje.necesarias}
                  {!a.aprendizaje.llega && " · no sale"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </Panel>

        {/*
          Creativos DENTRO de esta campaña: el mismo video puede rendir acá y no en otra, y
          agrupado por activo (no por anuncio) porque las copias se re-suben.
        */}
      <Panel title={`Creativos en esta campaña (${d.creativos.length})`} pad={0}>
          <div style={{ overflowX: "auto" }}>
          <table>
            <thead>
              <tr>
                <th style={{ textAlign: "left" }}>creativo</th>
                <th style={{ textAlign: "right" }}>% gasto</th>
                <th style={{ textAlign: "right" }}>gasto</th>
                <th style={{ textAlign: "right" }}>compras</th>
                <th style={{ textAlign: "right" }}>ROAS</th>
                <th style={{ textAlign: "right" }}>CPA</th>
                <th style={{ textAlign: "right" }}>CTR</th>
                <th style={{ textAlign: "right" }}>engan.</th>
                <th style={{ textAlign: "right" }}>freq</th>
              </tr>
            </thead>
            <tbody>
              {d.creativos.map((c) => (
                <tr key={c.clave}>
                  <td style={{ maxWidth: 220 }}>
                    <div style={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={c.nombre}>
                      {c.nombre}
                      {!c.corriendo && <span style={{ fontSize: 10, color: "#A0AABA", fontWeight: 400 }}> · apagado</span>}
                    </div>
                    <div style={{ fontSize: 10.5, color: "#A0AABA" }}>{c.clave}</div>
                  </td>
                  <N style={{ color: "#7A8699" }}>{pct(c.share, 0)}</N>
                  <N>{money(c.gasto)}</N>
                  <N>{c.compras}</N>
                  <N style={{ fontWeight: 700, color: roasColor(c.roas) }}>{Number(c.roas).toFixed(2)}</N>
                  <N>{precio(c.cpa)}</N>
                  <N>{pct(c.ctr)}</N>
                  <N>{pct(c.enganche, 1)}</N>
                  <N>{Number(c.frecuencia_max).toFixed(2)}</N>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
      </Panel>

        {/* País: el CPA objetivo sale del LTV de cada mercado, así que un promedio no alcanza. */}
      <Panel title={`Países (${d.paises.length})`} pad={0}>
          <div style={{ overflowX: "auto" }}>
          <table>
            <thead>
              <tr>
                <th style={{ textAlign: "left" }}>país</th>
                <th style={{ textAlign: "right" }}>% gasto</th>
                <th style={{ textAlign: "right" }}>gasto</th>
                <th style={{ textAlign: "right" }}>compras</th>
                <th style={{ textAlign: "right" }}>ingreso</th>
                <th style={{ textAlign: "right" }}>ROAS</th>
                <th style={{ textAlign: "right" }}>CPA</th>
                <th style={{ textAlign: "right" }}>CTR</th>
                <th style={{ textAlign: "right" }}>CPM</th>
              </tr>
            </thead>
            <tbody>
              {d.paises.map((p) => (
                <tr key={p.clave}>
                  <td style={{ fontWeight: 600 }}>{p.clave}</td>
                  <N style={{ color: "#7A8699" }}>{pct(p.share, 0)}</N>
                  <N>{money(p.gasto)}</N>
                  <N>{p.compras}</N>
                  <N>{p.valor > 0 ? money(p.valor) : "—"}</N>
                  <N style={{ fontWeight: 700, color: roasColor(p.roas) }}>{Number(p.roas).toFixed(2)}</N>
                  <N>{precio(p.cpa)}</N>
                  <N>{pct(p.ctr)}</N>
                  <N>{precio(p.cpm)}</N>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
      </Panel>

      <div style={{ fontSize: 11, color: "#A0AABA", marginBottom: 10 }}>{d.nota}</div>
    </div>
  );
}
